/**
 * New payment request — the full-width drawer.
 *
 * Wide because it holds three things at once: who it goes to, what it asks
 * for, and what the member will see. The preview is the point of the width:
 * an owner billing 40 parents should read the message before it is sent, not
 * after.
 *
 * Every figure in the footer and the preview comes from `newRequestSummary`,
 * which counts `recipientsFor` — so "Send to 7 members" is who will actually
 * be billed, not the size of the roster.
 *
 * Spec: DESIGN-BRIEF.md §E (New request) and `vNewReq()`.
 */
import React, {useMemo, useState} from 'react';
import {MagnifyingGlassIcon, PlusIcon, XMarkIcon} from '@heroicons/react/24/outline';
import {
  batchOf,
  isAttending,
  money,
  type NetlyPayData,
} from '../../../internals/managers/NetlyPayManager';
import {newRequestErrors, newRequestSummary, type NewRequestForm} from '../requests';
import {GhostButton, PrimaryButton} from '../components/Overlays';

const inputCls =
  'h-10 px-3 rounded-lg border border-borderc2 bg-surface text-[13px] text-text1 outline-none focus:border-brand transition-colors';
const label = 'text-[12px] font-semibold text-text2';

const Switch = ({on, onClick, title}: {on: boolean; onClick: () => void; title: string}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={title}
    onClick={onClick}
    className={`w-[38px] h-[22px] rounded-full p-[3px] flex-shrink-0 transition-colors ${
      on ? 'bg-brand' : 'bg-surface3'
    }`}
  >
    <span
      className={`block w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : ''}`}
      style={{boxShadow: '0 1px 2px rgba(15,15,26,.3)'}}
    />
  </button>
);

const Tick = ({on}: {on: boolean}) => (
  <span
    className={`w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center text-[11px] text-white flex-shrink-0 ${
      on ? 'bg-brand border-brand' : 'bg-surface border-borderc2'
    }`}
    aria-hidden="true"
  >
    {on ? '✓' : ''}
  </span>
);

export const NewRequestDrawer = ({
  data,
  form,
  currency = 'Rs.',
  busy,
  onChange,
  onClose,
  onSend,
  onSaveDraft,
}: {
  data: NetlyPayData;
  form: NewRequestForm;
  currency?: string;
  busy?: boolean;
  onChange: (patch: Partial<NewRequestForm>) => void;
  onClose: () => void;
  onSend: () => void;
  onSaveDraft: () => void;
}) => {
  const [attempted, setAttempted] = useState(false);
  const [q, setQ] = useState('');

  const summary = useMemo(() => newRequestSummary(data, form, currency), [data, form, currency]);
  const errors = useMemo(() => newRequestErrors(data, form), [data, form]);
  const showErrors = attempted && errors.length > 0;

  const toggleId = (id: string) =>
    onChange({ids: form.ids.includes(id) ? form.ids.filter((x) => x !== id) : form.ids.concat([id])});

  const memberResults = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data.members ?? [])
      .filter((m) => isAttending(m))
      .filter((m) => !needle || m.name.toLowerCase().includes(needle) || m.id.toLowerCase().includes(needle))
      .slice(0, 9);
  }, [data.members, q]);

  const groups = useMemo(() => {
    if (form.mode === 'batch') {
      return (data.batches ?? []).map((b) => ({
        id: b.id,
        name: b.name,
        sub: `${(data.members ?? []).filter((m) => m.batchId === b.id && isAttending(m)).length} members · ${money(
          b.feeMinor,
          currency,
        )}`,
      }));
    }
    return (data.programs ?? []).map((p) => {
      const batchIds = (data.batches ?? []).filter((b) => b.programId === p.id).map((b) => b.id);
      const members = (data.members ?? []).filter((m) => batchIds.includes(m.batchId) && isAttending(m));
      return {
        id: p.id,
        name: p.name,
        sub: `${batchIds.length} batches · ${members.length} members`,
      };
    });
  }, [data, form.mode, currency]);

  const send = () => {
    setAttempted(true);
    if (!errors.length) onSend();
  };

  const saveDraft = () => {
    setAttempted(true);
    if (form.title.trim()) onSaveDraft();
  };

  const optionFields = (
    fields: {pre: string; key: keyof NewRequestForm; post: string; width: string}[],
  ) => (
    <div className="flex items-center gap-3 flex-wrap mt-3">
      {fields.map((f) => (
        <span key={String(f.key)} className="flex items-center gap-2 text-[12.5px] text-text2">
          {f.pre}
          <input
            value={String(form[f.key] ?? '')}
            onChange={(e) => onChange({[f.key]: e.target.value} as Partial<NewRequestForm>)}
            aria-label={`${f.pre} ${f.post}`.trim()}
            className={`${inputCls} font-mono text-center`}
            style={{width: f.width}}
          />
          {f.post}
        </span>
      ))}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end"
      style={{background: 'rgba(15,15,26,.36)'}}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="New payment request"
        className="h-full w-full bg-surface flex flex-col"
        style={{maxWidth: 1120, boxShadow: '-12px 0 40px rgba(15,15,26,.16)'}}
      >
        <div className="px-7 pt-5 pb-4 border-b border-borderc flex items-start justify-between gap-4 flex-shrink-0">
          <div>
            <h2 className="text-[18px] font-semibold text-text1 leading-tight">
              {form.draftId ? `Edit draft ${form.draftId}` : 'New payment request'}
            </h2>
            <p className="text-[12.5px] text-text3 mt-1">{summary.recipientLine}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text3 hover:bg-surface2 hover:text-text1 transition-colors"
          >
            <XMarkIcon className="w-[18px] h-[18px]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px]">
            {/* The form */}
            <div className="px-7 py-6 flex flex-col gap-6">
              {showErrors && (
                <div className="rounded-xl border border-neg/40 bg-neg-soft/40 px-4 py-3">
                  <p className="text-[12.5px] font-semibold text-neg">
                    {errors.length === 1 ? 'One thing to fix' : `${errors.length} things to fix`}
                  </p>
                  <ul className="mt-1 text-[12.5px] text-text2 list-disc pl-4">
                    {errors.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recipients */}
              <section>
                <h3 className="text-[16px] font-semibold text-text1">Who receives this</h3>
                <div className="inline-flex p-[3px] rounded-[10px] bg-surface3 gap-0.5 mt-3">
                  {(['member', 'batch', 'program'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => {
                        if (form.mode !== mode) onChange({mode, ids: []});
                      }}
                      className={`h-8 px-3.5 rounded-lg text-[12.5px] font-semibold capitalize transition-colors ${
                        form.mode === mode ? 'bg-surface text-text1 shadow-sm' : 'text-text3'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>

                {form.mode === 'member' ? (
                  <div className="mt-3.5">
                    {form.ids.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap mb-2.5">
                        {form.ids.map((id) => {
                          const m = (data.members ?? []).find((x) => x.id === id);
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-full bg-brand-soft text-brand text-[12.5px] font-medium"
                            >
                              {m?.name ?? id}
                              <button
                                type="button"
                                onClick={() => toggleId(id)}
                                aria-label={`Remove ${m?.name ?? id}`}
                                className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-brand/20"
                              >
                                <XMarkIcon className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="relative">
                      <MagnifyingGlassIcon className="w-4 h-4 text-text3 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search a member by name or number"
                        aria-label="Search members"
                        className={`${inputCls} pl-9 w-full max-w-[420px]`}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 mt-3">
                      {memberResults.map((m) => {
                        const on = form.ids.includes(m.id);
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => toggleId(m.id)}
                            className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                              on ? 'border-brand bg-brand-soft/40' : 'border-borderc bg-surface hover:bg-surface2'
                            }`}
                          >
                            <span className="min-w-0">
                              <span className="block text-[13px] font-medium text-text1 truncate">{m.name}</span>
                              <span className="block text-[11.5px] text-text3 truncate">
                                {m.id} · {batchOf(data, m.batchId)?.name ?? '—'}
                              </span>
                            </span>
                            <span className="text-[13px] text-brand">{on ? '✓' : '+'}</span>
                          </button>
                        );
                      })}
                      {memberResults.length === 0 && (
                        <p className="text-[12.5px] text-text3">No member matches that search.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3.5">
                    {groups.map((g) => {
                      const on = form.ids.includes(g.id);
                      return (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => toggleId(g.id)}
                          className={`flex items-center gap-3 px-3.5 py-3 rounded-xl border text-left transition-colors ${
                            on ? 'border-brand bg-brand-soft/40' : 'border-borderc bg-surface hover:bg-surface2'
                          }`}
                        >
                          <Tick on={on} />
                          <span className="min-w-0">
                            <span className="block text-[13px] font-medium text-text1 truncate">{g.name}</span>
                            <span className="block text-[11.5px] text-text3 truncate">{g.sub}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-[12px] text-text3 mt-2.5">
                  Frozen, expired and members waiting for approval are left out.
                </p>
              </section>

              {/* What it asks for */}
              <section>
                <h3 className="text-[16px] font-semibold text-text1">What it asks for</h3>
                <div className="mt-3 flex flex-col gap-3 max-w-[560px]">
                  <label className="flex flex-col gap-1.5">
                    <span className={label}>Title</span>
                    <input
                      value={form.title}
                      onChange={(e) => onChange({title: e.target.value})}
                      placeholder="November fee"
                      className={`${inputCls} ${showErrors && !form.title.trim() ? 'border-neg' : ''}`}
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => onChange({useBatchFee: !form.useBatchFee})}
                    className={`flex items-center gap-3 px-3.5 py-3 rounded-xl border text-left transition-colors ${
                      form.useBatchFee ? 'border-brand bg-brand-soft/30' : 'border-borderc bg-surface'
                    }`}
                  >
                    <Tick on={form.useBatchFee} />
                    <span>
                      <span className="block text-[13px] font-medium text-text1">
                        Use each member&apos;s batch fee as the first line
                      </span>
                      <span className="block text-[11.5px] text-text3">{summary.feeRange}</span>
                    </span>
                  </button>

                  {form.items.map((item, ix) => (
                    <div key={ix} className="flex items-center gap-2">
                      <input
                        value={item.label}
                        onChange={(e) => {
                          const items = form.items.slice();
                          items[ix] = {...items[ix], label: e.target.value};
                          onChange({items});
                        }}
                        placeholder="Line item"
                        aria-label={`Line item ${ix + 1}`}
                        className={`${inputCls} flex-1`}
                      />
                      <input
                        value={item.amount}
                        onChange={(e) => {
                          const items = form.items.slice();
                          items[ix] = {...items[ix], amount: e.target.value};
                          onChange({items});
                        }}
                        inputMode="decimal"
                        placeholder="0.00"
                        aria-label={`Line item ${ix + 1} amount`}
                        className={`${inputCls} font-mono text-right w-[130px] ${
                          showErrors && !(Number(item.amount) > 0) ? 'border-neg' : ''
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => onChange({items: form.items.filter((_, j) => j !== ix)})}
                        aria-label={`Remove line item ${ix + 1}`}
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-text3 hover:bg-surface2 hover:text-neg"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => onChange({items: form.items.concat([{label: '', amount: ''}])})}
                    className="self-start inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-dashed border-borderc2 text-[12.5px] font-medium text-text2 hover:bg-surface2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add a line
                  </button>

                  <label className="flex flex-col gap-1.5 max-w-[220px]">
                    <span className={label}>Due date</span>
                    <input
                      type="date"
                      value={form.due}
                      onChange={(e) => onChange({due: e.target.value})}
                      className={`${inputCls} font-mono ${showErrors && !form.due ? 'border-neg' : ''}`}
                    />
                  </label>
                </div>
              </section>

              {/* Toggles */}
              <section>
                <h3 className="text-[16px] font-semibold text-text1">How it behaves</h3>
                <div className="mt-3 flex flex-col gap-2.5 max-w-[560px]">
                  <div
                    className={`rounded-xl border px-4 py-3.5 ${
                      form.repeat ? 'border-brand/40 bg-brand-soft/20' : 'border-borderc bg-surface'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span>
                        <span className="block text-[13px] font-semibold text-text1">Repeat monthly</span>
                        <span className="block text-[12px] text-text2">Sends a new request every month.</span>
                      </span>
                      <Switch on={form.repeat} title="Repeat monthly" onClick={() => onChange({repeat: !form.repeat})} />
                    </div>
                    {form.repeat &&
                      optionFields([
                        {pre: 'On day', key: 'repeatDay', post: 'of each month', width: '64px'},
                        {pre: 'Stop after', key: 'endsAfter', post: 'months · blank for no end', width: '72px'},
                      ])}
                  </div>

                  <div
                    className={`rounded-xl border px-4 py-3.5 ${
                      form.late ? 'border-brand/40 bg-brand-soft/20' : 'border-borderc bg-surface'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span>
                        <span className="block text-[13px] font-semibold text-text1">Late fee</span>
                        <span className="block text-[12px] text-text2">
                          Added automatically when the request is overdue.
                        </span>
                      </span>
                      <Switch on={form.late} title="Late fee" onClick={() => onChange({late: !form.late})} />
                    </div>
                    {form.late &&
                      optionFields([
                        {pre: currency, key: 'lateAmount', post: '', width: '100px'},
                        {pre: 'after', key: 'lateDays', post: 'days overdue', width: '64px'},
                      ])}
                  </div>

                  <div
                    className={`rounded-xl border px-4 py-3.5 ${
                      form.part ? 'border-brand/40 bg-brand-soft/20' : 'border-borderc bg-surface'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span>
                        <span className="block text-[13px] font-semibold text-text1">Allow part payment</span>
                        <span className="block text-[12px] text-text2">
                          Members can pay some now and the rest later.
                        </span>
                      </span>
                      <Switch on={form.part} title="Allow part payment" onClick={() => onChange({part: !form.part})} />
                    </div>
                    {form.part &&
                      optionFields([{pre: `Minimum ${currency}`, key: 'partMin', post: 'per payment', width: '100px'}])}
                  </div>

                  <label className="flex flex-col gap-1.5">
                    <span className={label}>Note to the member (optional)</span>
                    <textarea
                      value={form.note}
                      onChange={(e) => onChange({note: e.target.value})}
                      rows={2}
                      className="px-3 py-2.5 rounded-lg border border-borderc2 bg-surface text-[13px] text-text1 outline-none focus:border-brand transition-colors resize-none"
                    />
                  </label>
                </div>
              </section>
            </div>

            {/* The member-app preview */}
            <aside className="border-t lg:border-t-0 lg:border-l border-borderc bg-surface2 px-6 py-6">
              <p className="text-[11px] font-semibold tracking-[.06em] text-text3">
                WHAT THE MEMBER SEES
              </p>
              <div
                className="mt-3 rounded-[22px] border border-borderc bg-surface p-4"
                style={{boxShadow: '0 12px 32px rgba(15,15,26,.10)'}}
              >
                <p className="text-[12.5px] text-text2">{summary.preview.greeting}</p>
                <p className="text-[15px] font-semibold text-text1 mt-3">{summary.preview.title}</p>
                <p className="font-mono tabular-nums text-[22px] font-semibold text-text1 mt-1">
                  {summary.preview.total}
                </p>
                <p className="text-[12px] text-text3">Due {summary.preview.due}</p>

                <div className="mt-3.5 border-t border-borderc pt-3 flex flex-col gap-1.5">
                  {summary.preview.items.length === 0 && (
                    <p className="text-[12px] text-text3">Add a line to see it here.</p>
                  )}
                  {summary.preview.items.map((i, ix) => (
                    <div key={`${i.label}-${ix}`} className="flex items-center justify-between gap-3">
                      <span className="text-[12.5px] text-text2">{i.label}</span>
                      <span className="font-mono tabular-nums text-[12.5px] text-text1">{i.amount}</span>
                    </div>
                  ))}
                </div>

                {summary.preview.notes.length > 0 && (
                  <ul className="mt-3.5 border-t border-borderc pt-3 flex flex-col gap-1.5">
                    {summary.preview.notes.map((n) => (
                      <li key={n} className="text-[11.5px] text-text3">
                        {n}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-4 h-10 rounded-[9px] bg-brand text-white text-[13px] font-semibold flex items-center justify-center">
                  Pay now
                </div>
              </div>
              <p className="text-[11.5px] text-text3 mt-3">
                The first recipient is previewed. Each member is billed at their own batch fee.
              </p>
            </aside>
          </div>
        </div>

        <div className="px-7 py-4 border-t border-borderc bg-surface2 flex items-center justify-between gap-4 flex-wrap flex-shrink-0">
          <p className="text-[13px] font-medium text-text1">{summary.footLine}</p>
          <span className="flex items-center gap-2.5">
            <GhostButton disabled={busy} onClick={saveDraft}>
              Save draft
            </GhostButton>
            <PrimaryButton disabled={busy} onClick={send}>
              {summary.sendLabel}
            </PrimaryButton>
          </span>
        </div>
      </aside>
    </div>
  );
};

export default NewRequestDrawer;
